import { lazy, Suspense, useMemo } from 'react'
import type { ColDef } from 'ag-grid-community'
import chroma from 'chroma-js'

import {
  ClassLocationRowEntry,
  ConsequenceAreaRowEntry,
  IColourClassificationOptions,
  IHCAMCAClassLocationTableObject,
  ISelectOption
} from '$/types'
import { useAppSelector } from 'app/hooks'
import { chopAndFormatNumberWithCommas } from 'common/utils/chart/chartUtils'

import ToolbarContent from 'features/RegulatoryTools/HCAMCA/Components/ToolbarContent'
import { standardDateFormatter } from 'features/RiskManagement/Components/Tables/utils/formatters'
import { selectBaseSettings } from 'features/Settings/settingsSelectors'

import tailwind from '_/tailwind.config'

const QRATableWrapperLazy = lazy(
  () =>
    import(
      '../../../../../../common/components/QRATableWrapper/QRATableWrapper'
    )
)

interface IHCAMCATableView {
  tableData?: IHCAMCAClassLocationTableObject
  selectedLines: ISelectOption[]
  assessmentDateRange: Array<Date | null>
  selectedClassification: ISelectOption[]
  lineOptions: ISelectOption[]
  classificationOptions: ISelectOption[]
  setSelectedLines: (value: ISelectOption[]) => void
  setAssessmentDateRange: (value: Array<Date | null>) => void
  setSelectedClassification: (value: ISelectOption[]) => void
}

interface IParams {
  node: {
    data: ClassLocationRowEntry | ConsequenceAreaRowEntry
  }
  value: string
}

const THREE_DP_FIELDS = ['FromMeasure', 'ToMeasure', 'Length']

const HCAMCATableView = ({
  tableData,
  selectedLines,
  assessmentDateRange,
  selectedClassification,
  lineOptions,
  classificationOptions,
  setSelectedLines,
  setAssessmentDateRange,
  setSelectedClassification
}: IHCAMCATableView) => {
  const baseSettings = useAppSelector(selectBaseSettings)

  const { colDefs, rowData } = useMemo(() => {
    const classificationColourOptions: Array<{
      code: string
      name: string
      colour: string
    }> = []

    const lookupTables = baseSettings.find(
      (setting) => setting.groupName === 'LookupTables'
    )

    lookupTables?.details.forEach((table) => {
      if (table.key === 'ClassificationColour') {
        const classifications =
          table.value as unknown as IColourClassificationOptions[]

        classifications.forEach((classification) => {
          classificationColourOptions.push({
            code: classification.Code,
            name: classification.Name,
            colour: classification.Colour
          })
        })
      }
    })

    const getCodeFromName = (rowData: string) =>
      classificationColourOptions.find(
        (classification) => rowData === classification.name
      )?.code

    const findColourValue = (classificationValue: string): string => {
      const classificationOption = classificationColourOptions.find(
        (classification) => classificationValue === classification.code
      )

      return classificationOption?.colour
        ? '#' + classificationOption.colour
        : tailwind.theme.colors.lighthouse[500]
    }

    const getColorOfTypeTable = (
      classification: ConsequenceAreaRowEntry | ClassLocationRowEntry
    ): string => {
      if (
        Object.prototype.hasOwnProperty.call(
          classification,
          'ConsequenceAreaCode'
        )
      ) {
        const castedConsequenceArea = classification as ConsequenceAreaRowEntry

        return findColourValue(castedConsequenceArea.ConsequenceAreaCode)
      } else {
        const castedClassLocation = classification as ClassLocationRowEntry
        const code = getCodeFromName(castedClassLocation.ClassLocationName)

        return findColourValue(code ?? '')
      }
    }

    const renderClassification = (params: IParams) => {
      const backgroundColor = getColorOfTypeTable(params.node.data)

      return (
        <div
          className='rounded text-center font-medium'
          style={{ backgroundColor }}
        >
          <span
            className={
              chroma(backgroundColor).luminance() < 0.4 ? 'text-white' : ''
            }
          >
            {params.value}
          </span>
        </div>
      )
    }

    const renderRouteId = (params: IParams) => {
      return (
        <div
          className='rounded border-l-4 pl-4'
          style={{
            borderLeftColor: getColorOfTypeTable(params.node.data)
          }}
        >
          <span className='text-lighthouseBlue'>{params.value}</span>
        </div>
      )
    }

    const baseDefs: ColDef[] = []

    const mergedDefs = tableData?.columnDefinitions.reduce(
      (tableDefs, tableDef) => {
        const _colDef: ColDef = { ...tableDef }

        if (tableDef.headerName === 'Route ID') {
          _colDef.cellRenderer = renderRouteId
        } else if (tableDef.headerName === 'Classification') {
          _colDef.cellRenderer = renderClassification
        } else if (
          tableDef.field === 'AssessmentDate' ||
          tableDef.field === 'NextAssessment' ||
          tableDef.field === 'EffectiveFromDate'
        ) {
          _colDef.valueFormatter = standardDateFormatter
        } else if (THREE_DP_FIELDS.includes(tableDef.field)) {
          _colDef.valueFormatter = ({ value }) =>
            typeof value === 'number'
              ? chopAndFormatNumberWithCommas(value, {
                  minimumFractionDigits: 3,
                  maximumFractionDigits: 3
                })
              : (value ?? 'NULL')
        }

        // fixing it here for now
        if (tableDef.pinned === true) {
          _colDef.pinned = false
        }

        if (
          tableDef.headerName !== 'Colour' &&
          tableDef.headerName !== 'Route ID'
        ) {
          tableDefs.push(_colDef)
        }

        return tableDefs
      },
      baseDefs
    )

    return {
      colDefs: mergedDefs ?? [],
      rowData: tableData?.dataset1Rows.rows.map((row) => row.rowEntry) ?? []
    }
  }, [baseSettings, tableData?.columnDefinitions, tableData?.dataset1Rows.rows])

  return (
    <div className='flex h-full'>
      <div
        className={[
          'ag-theme-lighthouse',
          'flex flex-1 flex-col',
          'bg-white'
        ].join(' ')}
      >
        <ToolbarContent
          selectedLines={selectedLines}
          assessmentDateRange={assessmentDateRange}
          selectedClassification={selectedClassification}
          setSelectedLines={setSelectedLines}
          setAssessmentDateRange={setAssessmentDateRange}
          setSelectedClassification={setSelectedClassification}
          lineOptions={lineOptions}
          classificationOptions={classificationOptions}
        />
        <div className='ml-2 h-full'>
          <Suspense fallback='Loading table...'>
            <QRATableWrapperLazy colDefs={colDefs} rowData={rowData} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}

export default HCAMCATableView
